import React, { useState, useEffect } from 'react'
import { Input, Select, Button } from 'components/ui'
import { HiOutlineSearch, HiOutlineFilter, HiOutlineX, HiOutlineCalendar } from 'react-icons/hi'
import { DatePicker } from 'components/ui'
import dayjs from 'dayjs'
import { API_BASE_URL } from 'redux/constants'
import apiFetch from 'utils/apiFetch'

const buildDefaultYearOption = (year) =>
	year != null && year !== '' ? { value: String(year), label: String(year) } : null

const buildDefaultMonthOption = (month) => {
	if (month == null || month === '') return null
	const m = parseInt(month, 10)
	if (!Number.isFinite(m) || m < 1 || m > 12) return null
	return { value: String(m), label: dayjs().month(m - 1).format('MMMM') }
}

const METHOD_OPTIONS = [
    { value: '', label: 'All Methods' },
    { value: 'Biometric', label: 'Biometric' },
    { value: 'LPA App', label: 'LPA App' },
]

const SearchAndFilter = ({ onFilterChange, ministries = [], vendorOptions = [], locationOptions = [], showDuration = true, showMinistry = true, showDates = true, showVendor = false, showMethod = false, showLocation = false, showEmployeeFilters = false, showVendorFilters = false, showStatusOnly = false, showUserFilters = false, transactionStatuses = false, showYearMonth = false, showSearch = true, filtersAlwaysVisible = false, yearMonthFirst = false, allDeductions = [], defaultYear = null, defaultMonth = null, yearMonthClearable, ministryLocked = false, defaultMinistry = null, showPaymentRange = false, paymentRangeOptions = [], toolbarActions = null }) => {
    const resolvedYearMonthClearable =
        yearMonthClearable !== undefined ? yearMonthClearable : !(defaultYear && defaultMonth)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedMinistry, setSelectedMinistry] = useState(() => defaultMinistry || null)
    const [selectedVendor, setSelectedVendor] = useState(null)
    const [selectedMethod, setSelectedMethod] = useState(null)
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [duration, setDuration] = useState(null)
    const [gender, setGender] = useState(null)
    const [status, setStatus] = useState(null)
    const [position, setPosition] = useState(null)
    const [country, setCountry] = useState(null)
    const [userType, setUserType] = useState(null)
    const [maVendor, setMaVendor] = useState(null)
    const [role, setRole] = useState(null)
    const [selectedYear, setSelectedYear] = useState(() => buildDefaultYearOption(defaultYear))
    const [selectedMonth, setSelectedMonth] = useState(() => buildDefaultMonthOption(defaultMonth))
    const [selectedPaymentRange, setSelectedPaymentRange] = useState(null)
    const [showFilters, setShowFilters] = useState(filtersAlwaysVisible)

    // Prepare ministry options
    const ministryOptions = ministryLocked && defaultMinistry
        ? [defaultMinistry]
        : [
            { value: '', label: 'All Ministries/Agencies' },
            ...ministries.map(ma => ({
                value: ma.id || ma.name,
                label: ma.name || ma.shortName
            }))
        ]

    useEffect(() => {
        if (defaultMinistry) {
            setSelectedMinistry(defaultMinistry)
        }
    }, [defaultMinistry?.value, defaultMinistry?.label])

    const durationOptions = [
        { value: '', label: 'All Durations' },
        { value: '3', label: '3 months' },
        { value: '6', label: '6 months' }
    ]

    const genderOptions = [
        { value: '', label: 'All Genders' },
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' }
    ]

    const statusOptions = transactionStatuses ? [
        { value: '', label: 'All Status' },
        { value: 'paid', label: 'Paid' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'cancel', label: 'Cancel' },
        { value: 'progress', label: 'Progress' },
        { value: 'in review', label: 'In Review' }
    ] : [
        { value: '', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ]

    const positionOptions = [
        { value: '', label: 'All Positions' },
        { value: 'Director', label: 'Director' },
        { value: 'Manager', label: 'Manager' },
        { value: 'Supervisor', label: 'Supervisor' },
        { value: 'Coordinator', label: 'Coordinator' },
        { value: 'Assistant', label: 'Assistant' },
        { value: 'Officer', label: 'Officer' },
        { value: 'Specialist', label: 'Specialist' }
    ]

    // Liberia counties for vendor filter
    const countryOptions = [
        { value: '', label: 'All Countries' },
        { value: 'Bomi', label: 'Bomi'},
        { value: 'Bong', label: 'Bong'},
        { value: 'Gbarpolu', label: 'Gbarpolu'},
        { value: 'Grand Bassa', label: 'Grand Bassa'},
        { value: 'Grand Cape Mount', label: 'Grand Cape Mount'},
        { value: 'Grand Gedeh', label: 'Grand Gedeh'},
        { value: 'Grand Kru', label: 'Grand Kru'},
        { value: 'Lofa', label: 'Lofa'},
        { value: 'Margibi', label: 'Margibi'},
        { value: 'Maryland', label: 'Maryland'},
        { value: 'Montserrado', label: 'Montserrado'},
        { value: 'Nimba', label: 'Nimba'},
        { value: 'Rivercess', label: 'Rivercess'},
        { value: 'River Gee', label: 'River Gee'},
        { value: 'Sinoe', label: 'Sinoe'},
    ]

    const userTypeOptions = [
        { value: '', label: 'All User Types' },
        { value: 'MinistryAgency', label: 'MinistryAgency' },
        { value: 'Vendor', label: 'Vendor' },
        { value: 'SystemAdmin', label: 'System Admin' },
        { value: 'Bank', label: 'Bank' },
        { value: 'Admin', label: 'Admin' }
    ]

    // State for dynamically fetched roles
    const [roleOptions, setRoleOptions] = useState([
        { value: '', label: 'All Roles' }
    ])

    // Generate year options: from 2025 to current year (do not show next year)
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear()
        const startYear = 2025
        const years = []
        
        // Add years from startYear to currentYear
        for (let year = currentYear; year >= startYear; year--) {
            years.push({ value: String(year), label: String(year) })
        }
        
        return [
            { value: '', label: 'Select Year' },
            ...years
        ]
    }

    // Generate month options
    const getMonthOptions = () => {
        const months = [
            { value: '1', label: 'January' },
            { value: '2', label: 'February' },
            { value: '3', label: 'March' },
            { value: '4', label: 'April' },
            { value: '5', label: 'May' },
            { value: '6', label: 'June' },
            { value: '7', label: 'July' },
            { value: '8', label: 'August' },
            { value: '9', label: 'September' },
            { value: '10', label: 'October' },
            { value: '11', label: 'November' },
            { value: '12', label: 'December' }
        ]
        
        return [
            { value: '', label: 'Select Month' },
            ...months
        ]
    }

    const yearOptions = getYearOptions()
    const monthOptions = getMonthOptions()

    // Fetch roles from API when showUserFilters is true
    useEffect(() => {
        if (showUserFilters) {
            const fetchRoles = async () => {
                try {
                    const response = await apiFetch(`${API_BASE_URL}SysConfig/GetAllRoles`, { 
                        method: 'GET' 
                    });
                    const json = await response.json();
                    const items = Array.isArray(json) ? json : [];
                    const mappedRoles = items.map(item => ({ 
                        value: item.name, 
                        label: item.name 
                    }));
                    // Add "All Roles" at the beginning
                    setRoleOptions([
                        { value: '', label: 'All Roles' },
                        ...mappedRoles
                    ]);
                } catch (error) {
                    console.error('Error loading roles:', error);
                    // Keep the default "All Roles" option if fetch fails
                }
            };

            fetchRoles();
        }
    }, [showUserFilters])

    useEffect(() => {
        const filters = {
            searchQuery,
            selectedMinistry: selectedMinistry?.value,
            selectedVendor: selectedVendor?.value,
            selectedMethod: selectedMethod?.value || '',
            selectedLocation: selectedLocation?.value,
            startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
            endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
            duration: duration?.value,
            gender: gender?.value,
            status: status?.value,
            position: position?.value,
            country: country?.value,
            userType: userType?.value,
            maVendor: maVendor?.value,
            role: role?.value,
            selectedYear: selectedYear?.value,
            selectedMonth: selectedMonth?.value,
            selectedPaymentRange: selectedPaymentRange?.value ?? ''
        }
        onFilterChange(filters)
    }, [searchQuery, selectedMinistry, selectedVendor, selectedMethod, selectedLocation, startDate, endDate, duration, gender, status, position, country, userType, maVendor, role, selectedYear, selectedMonth, selectedPaymentRange, onFilterChange])

    const handleClearFilters = () => {
        setSearchQuery('')
        if (!ministryLocked) {
            setSelectedMinistry(null)
        } else if (defaultMinistry) {
            setSelectedMinistry(defaultMinistry)
        }
        setSelectedVendor(null)
        setSelectedMethod(null)
        setSelectedLocation(null)
        setStartDate(null)
        setEndDate(null)
        setDuration(null)
        setGender(null)
        setStatus(null)
        setPosition(null)
        setCountry(null)
        setUserType(null)
        setMaVendor(null)
        setRole(null)
        if (resolvedYearMonthClearable) {
            setSelectedYear(null)
            setSelectedMonth(null)
        } else {
            setSelectedYear(buildDefaultYearOption(defaultYear))
            setSelectedMonth(buildDefaultMonthOption(defaultMonth))
        }
        setSelectedPaymentRange(null)
    }

    const hasActiveFilters =
        searchQuery ||
        (!ministryLocked && selectedMinistry) ||
        selectedVendor || selectedMethod || selectedLocation || startDate || endDate || duration || gender || status || position || country || userType || maVendor || role || selectedYear || selectedMonth || selectedPaymentRange

    const handleSearchChange = (e) => {
        const newQuery = e.target.value
        setSearchQuery(newQuery)
        
        // If search query is cleared, remove all filters
        if (newQuery.trim() === '') {
            handleClearFilters()
        }
    }

    return (
        <div className="mb-6 space-y-4">
            {/* Search Bar */}
            {!(filtersAlwaysVisible && !showSearch) && (
            <div className="flex items-center gap-4 flex-wrap">
                {showSearch && (
                <div className="flex-1 min-w-[300px]">
                    <Input
                        placeholder="Search"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="w-full"
                    />
                </div>
                )}
                
                {/* Only show filter buttons if there are filters available */}
                {toolbarActions}

                {!filtersAlwaysVisible && (showMinistry || showDates || showDuration || showVendor || showMethod || showLocation || showEmployeeFilters || showVendorFilters || showStatusOnly || showUserFilters || showYearMonth || showPaymentRange) && (
                    <Button
                        variant="outline"
                        size="sm"
                        icon={showFilters ? <HiOutlineFilter /> : <HiOutlineFilter />}
                        onClick={() => setShowFilters(!showFilters)}
                        active={showFilters}
                        className="h-[44px] flex items-center px-5"
                    >
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                )}

                {hasActiveFilters && (
                    <Button
                        variant="outline"
                        size="sm"
                        icon={<HiOutlineX />}
                        onClick={handleClearFilters}
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 h-[44px] flex items-center px-5"
                    >
                        Clear Filters
                    </Button>
                )}
            </div>
            )}

            {/* Filter Options */}
            {(filtersAlwaysVisible || showFilters) && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {showEmployeeFilters ? (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Gender
                                    </label>
                                    <Select
                                        placeholder="Select Gender"
                                        options={genderOptions}
                                        value={gender}
                                        onChange={setGender}
                                        isClearable
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Status
                                    </label>
                                    <Select
                                        placeholder="Select Status"
                                        options={statusOptions}
                                        value={status}
                                        onChange={setStatus}
                                        isClearable
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Ministry/Agency
                                    </label>
                                    <Select
                                        placeholder="Select Ministry/Agency"
                                        options={ministryOptions}
                                        value={selectedMinistry}
                                        onChange={setSelectedMinistry}
                                        isSearchable
                                        isClearable
                                    />
                                </div>
                            </>
                        ) : showVendorFilters ? (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Country
                                    </label>
                                    <Select
                                        placeholder="Select Country"
                                        options={countryOptions}
                                        value={country}
                                        onChange={setCountry}
                                        isClearable
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Status
                                    </label>
                                    <Select
                                        placeholder="Select Status"
                                        options={statusOptions}
                                        value={status}
                                        onChange={setStatus}
                                        isClearable
                                    />
                                </div>
                            </>
                        ) : showStatusOnly ? (
                            <>
                                {showDates && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Start Date
                                            </label>
                                            <DatePicker
                                                inputtable
                                                clearable
                                                placeholder="Select Start Date"
                                                value={startDate}
                                                onChange={(date) => setStartDate(date)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                End Date
                                            </label>
                                            <DatePicker
                                                inputtable
                                                clearable
                                                placeholder="Select End Date"
                                                value={endDate}
                                                onChange={(date) => setEndDate(date)}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Status
                                    </label>
                                    <Select
                                        placeholder="Select Status"
                                        options={statusOptions}
                                        value={status}
                                        onChange={setStatus}
                                        isClearable
                                    />
                                </div>
                                {showMethod && (
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Method
                                        </label>
                                        <Select
                                            placeholder="Select Method"
                                            options={METHOD_OPTIONS}
                                            value={selectedMethod}
                                            onChange={setSelectedMethod}
                                            isClearable
                                        />
                                    </div>
                                )}
                            </>
                        ) : showUserFilters ? (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        UserType
                                    </label>
                                    <Select
                                        placeholder="Select UserType"
                                        options={userTypeOptions}
                                        value={userType}
                                        onChange={setUserType}
                                        isClearable
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        M&A/Vendor
                                    </label>
                                    <Select
                                        placeholder="Select M&A/Vendor"
                                        options={ministryOptions}
                                        value={maVendor}
                                        onChange={setMaVendor}
                                        isSearchable
                                        isClearable
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Role
                                    </label>
                                    <Select
                                        placeholder="Select Role"
                                        options={roleOptions}
                                        value={role}
                                        onChange={setRole}
                                        isClearable
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {yearMonthFirst && showYearMonth && (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Year</label>
                                    <Select placeholder="Select Year" options={yearOptions} value={selectedYear} onChange={setSelectedYear} isClearable={resolvedYearMonthClearable} />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Month</label>
                                    <Select placeholder="Select Month" options={monthOptions} value={selectedMonth} onChange={setSelectedMonth} isClearable={resolvedYearMonthClearable} />
                                </div>
                            </>
                        )}
                                {showMinistry && (
                            <div className="space-y-2 md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Ministry/Agency
                                </label>
                                <Select
                                    placeholder="Select Ministry/Agency"
                                    options={ministryOptions}
                                    value={selectedMinistry}
                                    onChange={setSelectedMinistry}
                                    isSearchable={!ministryLocked}
                                    isClearable={!ministryLocked}
                                    isDisabled={ministryLocked}
                                />
                            </div>
                        )}

                        {showDates && (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Start Date
                                    </label>
                                    <DatePicker
                                        inputtable
                                        clearable
                                        placeholder="Select Start Date"
                                        value={startDate}
                                        onChange={(date) => setStartDate(date)}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        End Date
                                    </label>
                                    <DatePicker
                                        inputtable
                                        clearable
                                        placeholder="Select End Date"
                                        value={endDate}
                                        onChange={(date) => setEndDate(date)}
                                    />
                                </div>

                                {showPaymentRange && (
                                    <div className="space-y-2 md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Payment Range
                                        </label>
                                        <Select
                                            placeholder="Select Payment Range"
                                            options={paymentRangeOptions}
                                            value={selectedPaymentRange}
                                            onChange={setSelectedPaymentRange}
                                            isSearchable
                                            isClearable
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {showLocation && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Location
                                </label>
                                <Select
                                    placeholder="Select Location"
                                    options={locationOptions}
                                    value={selectedLocation}
                                    onChange={setSelectedLocation}
                                    isSearchable
                                    isClearable
                                />
                            </div>
                        )}

                        {showVendor && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Vendor
                                </label>
                                <Select
                                    placeholder="Select Vendor"
                                    options={vendorOptions}
                                    value={selectedVendor}
                                    onChange={setSelectedVendor}
                                    isSearchable
                                    isClearable
                                />
                            </div>
                        )}

                        {showMethod && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Method
                                </label>
                                <Select
                                    placeholder="Select Method"
                                    options={METHOD_OPTIONS}
                                    value={selectedMethod}
                                    onChange={setSelectedMethod}
                                    isClearable
                                />
                            </div>
                        )}
                            </>
                        )}

                        {showDuration && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Duration
                                </label>
                                <Select
                                    placeholder="Select Duration"
                                    options={durationOptions}
                                    value={duration}
                                    onChange={setDuration}
                                    isClearable
                                />
                            </div>
                        )}

                        {showYearMonth && !yearMonthFirst && (
                            <>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Year
                                    </label>
                                    <Select
                                        placeholder="Select Year"
                                        options={yearOptions}
                                        value={selectedYear}
                                        onChange={setSelectedYear}
                                        isClearable={resolvedYearMonthClearable}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Month
                                    </label>
                                    <Select
                                        placeholder="Select Month"
                                        options={monthOptions}
                                        value={selectedMonth}
                                        onChange={setSelectedMonth}
                                        isClearable={resolvedYearMonthClearable}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default SearchAndFilter

